import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { selectAuthToken } from "containers/App/selectors";
import { useSelector } from "react-redux";
import { authedRequest } from "utils/api/requests";
import { env } from "utils/env";

// Token expires after 1 hour (matches typical server-side session duration)
const TOKEN_EXPIRY_SECONDS = 3600;

export const useShareMutation = (gif, shareType, destination) => {
  const formattedDestination =
    shareType === "phone" ? destination.replace(/[^0-9]/g, "") : destination;
  return useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append("share_group[gif_id]", gif.id);
      formData.append("share_group[device_type]", "web");
      formData.append(
        "share_group[shares_attributes][0][share_type]",
        shareType.toLowerCase()
      );
      formData.append(
        "share_group[shares_attributes][0][content]",
        formattedDestination
      );

      return axios.post(
        `${env.VITE_SHARE_API_ROOT}/share_groups`,
        formData
      );
    },
    mutationKey: ["share", shareType],
  });
};

// Helper functions for channel-specific unlock token storage
const getUnlockTokenKey = (slug) => `unlock_token_${slug}`;

export const storeUnlockToken = (slug, token, expirySeconds = TOKEN_EXPIRY_SECONDS) => {
  const expiryTime = Date.now() + expirySeconds * 1000;
  const tokenData = JSON.stringify({ token, expiryTime });
  localStorage.setItem(getUnlockTokenKey(slug), tokenData);
};

export const getUnlockToken = (slug) => {
  const storedData = localStorage.getItem(getUnlockTokenKey(slug));
  if (!storedData) return null;

  try {
    const { token, expiryTime } = JSON.parse(storedData);

    // Check if token has expired
    if (Date.now() > expiryTime) {
      clearUnlockToken(slug); // Clean up expired token
      return null;
    }

    return token;
  } catch (e) {
    // Handle legacy tokens (plain strings without expiry)
    // or malformed data - clear and return null
    clearUnlockToken(slug);
    return null;
  }
};

export const clearUnlockToken = (slug) => {
  localStorage.removeItem(getUnlockTokenKey(slug));
};

export const useGetGallerySessionToken = (channelSlug, password) => {
  return useMutation({
    mutationFn: async () => {
      // V3 unlock endpoint uses slug in the URL path
      const response = await axios.post(
        `${env.VITE_API_ROOT}/v3/channels/${channelSlug}/unlock`,
        { password }
      );
      return response.data;
    },
    mutationKey: ["gallerySessionToken", channelSlug],
    onSuccess: (data) => {
      // V3 returns unlock_token (signed JWT-style token)
      // Store with channel-specific key so tokens for different galleries don't interfere
      if (data.unlock_token) {
        storeUnlockToken(channelSlug, data.unlock_token);
      }
    },
  });
};

export function useDeleteGifMutation() {
  const authToken = useSelector(selectAuthToken());

  return useMutation({
    mutationFn: (gifId) => {
      if (!authToken) {
        throw new Error("No auth token available");
      }
      return authedRequest(`v2/gifs/${gifId}`, authToken, {
        method: "DELETE",
      });

    },
    mutationKey: ["deleteGif"],
  });
}
